import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Download, FileText, LogOut, Megaphone, Users } from "lucide-react";
import { tenantStore, type Tenant } from "@/lib/tenants/tenantStore";
import { useLanguage } from "@/hooks/useLanguage";
import { LanguageDropdown } from "@/components/i18n/LanguageDropdown";
import { UniversalFilterBar } from "@/components/admin/UniversalFilterBar";
import { trackEvent } from "@/lib/audit";
import { PartnerFilters } from "@/components/partner/PartnerFilters";
import { PartnerPortalTabs } from "@/components/partner/PartnerPortalTabs";
import { getPartnerRole, PARTNER_ROLES, setPartnerRole } from "@/lib/partner/permissions";

export const Route = createFileRoute("/tenant-portal")({
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    // Hard gate (Phase 3): only superadmin OR an authenticated partner
    // session (legacy localStorage partner login) may reach the portal.
    const { supabase } = await import("@/integrations/supabase/client");
    const { data: sessionData } = await supabase.auth.getSession();
    let allowed = false;
    if (sessionData.session) {
      try {
        const { data } = await supabase.rpc("is_superadmin");
        if (data) allowed = true;
      } catch { /* noop */ }
    }
    if (!allowed) {
      try {
        const partner = window.localStorage.getItem("vs:partner_session");
        if (partner) allowed = true;
      } catch { /* noop */ }
    }
    if (!allowed) {
      throw redirect({ to: "/partners/login" as const });
    }
  },
  validateSearch: (search: Record<string, unknown>) => ({
    tenant: typeof search.tenant === "string" ? search.tenant : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Tenant Portal · VerseSport™" },
      { name: "description", content: "White-label tenant administration portal." },
    ],
  }),
  component: TenantPortalPage,
});

function TenantPortalPage() {
  const { t } = useLanguage();
  const search = Route.useSearch();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);

  useEffect(() => {
    const list = tenantStore.list();
    setTenants(list);
    // PR-12.1: never show a dead screen. Resolve in order:
    // 1) ?tenant= search param  2) active impersonation  3) Univision demo  4) first tenant
    const wanted =
      search.tenant ?? tenantStore.currentImpersonation() ?? "tenant_univision";
    const found =
      tenantStore.get(wanted) ??
      list.find((x) => x.slug === "univision") ??
      list[0] ??
      null;
    setTenant(found);
    if (found) void trackEvent("tenant_portal_viewed", { tenant_id: found.tenant_id });
  }, [search.tenant]);

  const isImpersonating = useMemo(() => Boolean(tenantStore.currentImpersonation()), []);

  if (!tenant) {
    return (
      <div className="min-h-screen bg-background text-foreground p-8 text-center text-sm">
        Loading partner portal…
      </div>
    );
  }

  const exportReport = (kind: "csv" | "json") => {
    const payload = {
      tenant_id: tenant.tenant_id,
      tenant_name: tenant.tenant_name,
      generated_at: new Date().toISOString(),
      kpis: { campaigns: 0, sessions: 0, conversions: 0 },
    };
    const blob =
      kind === "csv"
        ? new Blob(
            [
              "tenant_id,tenant_name,generated_at,campaigns,sessions,conversions\n" +
                `${payload.tenant_id},${payload.tenant_name},${payload.generated_at},0,0,0\n`,
            ],
            { type: "text/csv" },
          )
        : new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${tenant.slug}-report.${kind}`;
    a.click();
    URL.revokeObjectURL(url);
    void trackEvent("tenant_report_exported", { tenant_id: tenant.tenant_id, kind });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {isImpersonating && (
        <div className="bg-[oklch(0.78_0.18_70)] px-4 py-1.5 text-center text-[11px] font-black text-black">
          ⚠ {t("tp_impersonating", { name: tenant.tenant_name })}
          <button
            onClick={() => {
              tenantStore.endImpersonation();
              window.close();
            }}
            className="ml-3 inline-flex items-center gap-1 rounded bg-black/80 px-2 py-0.5 text-[10px] font-black text-white"
          >
            <LogOut className="h-3 w-3" /> {t("tp_end_impersonation")}
          </button>
        </div>
      )}
      <header
        className="sticky top-0 z-30 border-b border-border/60"
        style={{ background: `linear-gradient(90deg, ${tenant.branding.primary_color}22, transparent)` }}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="flex h-9 w-9 items-center justify-center rounded-lg glass hover:bg-accent"
              aria-label="Back"
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <div className="flex items-center gap-2">
              {tenant.branding.logo_url ? (
                <img src={tenant.branding.logo_url} alt={tenant.tenant_name} className="h-8 w-8 rounded" />
              ) : (
                <span
                  className="inline-block h-8 w-8 rounded"
                  style={{ background: tenant.branding.primary_color }}
                />
              )}
              <div>
                <div className="text-sm font-black tracking-tight">{tenant.tenant_name}</div>
                <div className="text-[10px] font-bold tracking-widest text-muted-foreground">
                  {t("tp_portal_subtitle")}
                </div>
              </div>
            </div>
          </div>
          <span
            className="rounded-full px-2 py-0.5 text-[9px] font-black tracking-widest"
            style={{ background: `${tenant.branding.primary_color}33`, color: tenant.branding.primary_color }}
          >
            {tenant.license.license_type.toUpperCase()}
          </span>
          {/* PR-12.1: partner switcher (visible to anyone with portal access). */}
          <select
            value={tenant.tenant_id}
            onChange={(e) => {
              const next = tenantStore.get(e.target.value);
              if (next) {
                setTenant(next);
                void trackEvent("tenant_portal_switched", { tenant_id: next.tenant_id });
              }
            }}
            className="ml-2 rounded-md bg-background/60 px-2 py-1 text-[11px] font-bold"
            aria-label="Select partner"
          >
            {tenants.map((tn) => (
              <option key={tn.tenant_id} value={tn.tenant_id}>
                {tn.tenant_name}
              </option>
            ))}
          </select>
          <LanguageDropdown className="ml-2" />
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-4 px-4 py-6">
        <UniversalFilterBar scope={`tenant_portal:${tenant.slug}`} />
        <PartnerFilters />
        <div className="flex items-center gap-2 rounded-2xl glass px-3 py-2 text-[11px]">
          <span className="font-bold">Rol activo:</span>
          <select
            defaultValue={getPartnerRole()}
            onChange={(e) => {
              setPartnerRole(e.target.value as ReturnType<typeof getPartnerRole>, tenant.tenant_id);
              window.location.reload();
            }}
            className="rounded-md bg-background/60 px-2 py-1 text-[11px] font-bold"
          >
            {PARTNER_ROLES.map((r) => (
              <option key={r} value={r}>{r.replace("_", " ").toUpperCase()}</option>
            ))}
          </select>
          <span className="text-muted-foreground">— ajusta desde /admin → Partner Portal Permissions</span>
        </div>
        <PartnerPortalTabs tenant={tenant} />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <KPI label={t("tp_kpi_campaigns")} value="0" />
          <KPI label={t("tp_kpi_sessions")} value="0" />
          <KPI label={t("tp_kpi_conversions")} value="0" />
          <KPI label={t("tp_kpi_seats")} value={`${tenant.users.length}/${tenant.license.seats}`} />
        </div>

        <section className="rounded-2xl glass p-4">
          <div className="mb-2 flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-primary" />
            <div className="text-sm font-black">{t("tp_campaigns")}</div>
          </div>
          <div className="text-[12px] text-muted-foreground">{t("tp_campaigns_empty")}</div>
        </section>

        <section className="rounded-2xl glass p-4">
          <div className="mb-2 flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            <div className="text-sm font-black">{t("tp_team")}</div>
          </div>
          <div className="space-y-1.5">
            {tenant.users.map((u) => (
              <div
                key={u.id}
                className="flex items-center justify-between rounded-md bg-background/40 px-2.5 py-1.5 text-[12px]"
              >
                <span>
                  <span className="font-bold">{u.name}</span>{" "}
                  <span className="text-muted-foreground">· {u.email}</span>
                </span>
                <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] font-black tracking-widest">
                  {u.role.replace("tenant_", "").toUpperCase()}
                </span>
              </div>
            ))}
            {tenant.users.length === 0 && (
              <div className="text-[12px] text-muted-foreground">{t("tp_team_empty")}</div>
            )}
          </div>
        </section>

        <section className="rounded-2xl glass p-4">
          <div className="mb-2 flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            <div className="text-sm font-black">{t("tp_reports")}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => exportReport("csv")}
              className="inline-flex items-center gap-1.5 rounded-lg glass px-3 py-1.5 text-[11px] font-black hover:bg-accent"
            >
              <Download className="h-3.5 w-3.5" /> CSV
            </button>
            <button
              onClick={() => exportReport("json")}
              className="inline-flex items-center gap-1.5 rounded-lg glass px-3 py-1.5 text-[11px] font-black hover:bg-accent"
            >
              <Download className="h-3.5 w-3.5" /> JSON
            </button>
          </div>
          <div className="mt-2 text-[10px] text-muted-foreground">{t("tp_reports_scope")}</div>
        </section>

        <footer className="pt-2 text-center text-[10px] text-muted-foreground">
          {tenant.branding.legal_text}
        </footer>
      </main>
    </div>
  );
}

function KPI({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl glass p-3">
      <div className="text-[9px] font-bold tracking-widest text-muted-foreground">{label.toUpperCase()}</div>
      <div className="mt-1 text-2xl font-black tabular-nums">{value}</div>
    </div>
  );
}
